<script lang="ts" setup>
	import FavoriteToggle from '@/components/favorites/FavoriteToggle.vue';
	import PaginationComponent from '@/components/generic/PaginationComponent.vue';
	import SearchComponent from '@/components/generic/SearchComponent.vue';
	import ProductDetailsGrid from '@/components/ProductDetailsGrid.vue';
	import ProductDetailsList from '@/components/ProductDetailsList.vue';
	import {useProductTableStore} from '@/store/table.store';
	import NoResultsComponent from '@/components/NoResultsComponent.vue';
	import {computed} from 'vue';
	import TableViewToggle from '@/components/table/TableViewToggle.vue';
	import type {StaticTableHeader} from '@/types/table';
	import type {Product} from 'dummen-shared';
	import CopyUrlButton from '@/components/generic/CopyUrlButton.vue';
	import {DetailView} from '@/enum/detail.view';
	import FilterToggle from '@/components/generic/FilterToggle.vue';
	import ClearFavoritesButton from '@/components/favorites/ClearFavoritesButton.vue';
	import {useFavoriteProductStore} from '@/store/favorite.product.store';
	import {storeToRefs} from 'pinia';

	defineProps<{
		products: Array<Product>;
		productHeaders: Array<StaticTableHeader>;
		detailHeaders: Record<string, string>;
		numberOfResults: number;
	}>();

	const emit = defineEmits(['on-update-data']);

	const tableStore = useProductTableStore();
	const favoriteStore = useFavoriteProductStore();

	const loading = computed(() => tableStore.loading);

	const detailView = computed(() => tableStore.detailView);

	const {favoriteViewActive} = storeToRefs(favoriteStore);

	const toggleView = (view: DetailView): void => tableStore.setDetailView(view);

	const updateData = async () => emit('on-update-data');
</script>

<template>
	<div class="catalog-overview">
		<div class="catalog-overview-header">
			<FilterToggle />

			<SearchComponent @search-updated="updateData" />

			<div v-if="numberOfResults" class="catalog-overview-header__title">
				<span>{{ $t('site.products.overview.header.results.partbeforeamount.title') }}</span>&nbsp;
				<strong v-text="numberOfResults" />&nbsp;
				<span>{{ $t('site.products.overview.header.results.partafteramount.title') }}</span>
			</div>

			<div class="catalog-overview-header__icons">
				<TableViewToggle :detail-view="detailView" @toggle-view="toggleView" />

				<FavoriteToggle @favorites-toggled="updateData" />

				<ClearFavoritesButton v-if="favoriteViewActive" @favorites-cleared="updateData" />

				<CopyUrlButton />
			</div>
		</div>

		<NoResultsComponent :loading="loading" :number-of-products="products.length" @reset-filters="updateData" />

		<template v-if="!loading && products.length > 0">
			<ProductDetailsList v-if="detailView === DetailView.LIST"
			                    :detail-headers="detailHeaders"
			                    :product-headers="productHeaders"
			                    :products="products"
			                    @on-update-sort="updateData" />

			<ProductDetailsGrid v-else :products="products" />

			<PaginationComponent @pagination-updated="updateData" />
		</template>
	</div>
</template>
